/* src/modules/office/components/DeviationApprovalPanel.js */
import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, query, where, getDocs, updateDoc, doc, serverTimestamp, orderBy, limit 
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { 
    CheckCircleIcon, XCircleIcon, ClockIcon, SearchIcon, 
    ChevronLeftIcon, ChevronRightIcon 
} from '../../../components/icons/Icons';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { formatDateLocal } from '../utils/dateHelpers';

// Helper to format timestamps (Created/Actioned At)
const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export default function DeviationApprovalPanel({ userProfile, appId }) {
    const [incomingRequests, setIncomingRequests] = useState([]);
    const [myRequests, setMyRequests] = useState([]);
    const [companyRequests, setCompanyRequests] = useState([]); // Admin View Data
    const [loading, setLoading] = useState(true);
    
    // UI State
    const [activeTab, setActiveTab] = useState('incoming'); // 'incoming' | 'outgoing' | 'company'
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10); 

    const isHRAdmin = userProfile.role === 'Admin' || userProfile.department === 'HR';

    // 1. Fetch Data
    const fetchRequests = async () => {
        setLoading(true);
        try {
            const devRef = collection(db, `artifacts/${appId}/schedule_deviations`);
            
            // --- A. To Approve (Standardized for ALL Managers, including HR) ---
            // Fetch ALL requests sent to this user (Pending + History)
            // This ensures HR/Admins see their own team's full history here, just like any other manager.
            const qIncoming = query(
                devRef, 
                where("managerId", "==", userProfile.id), 
                orderBy("createdAt", "desc")
            );

            // --- B. My Request History (Standardized) ---
            const qOutgoing = query(
                devRef, 
                where("userId", "==", userProfile.id), 
                orderBy("createdAt", "desc")
            );

            const queries = [getDocs(qIncoming), getDocs(qOutgoing)];
            
            // --- C. Company Requests (Admin Oversight Only) ---
            if (isHRAdmin) {
                // Fetch recent 500 records for oversight
                const qCompany = query(devRef, orderBy("createdAt", "desc"), limit(500));
                queries.push(getDocs(qCompany));
            }

            const results = await Promise.all(queries);
            
            const mapDocs = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            setIncomingRequests(mapDocs(results[0]));
            setMyRequests(mapDocs(results[1]));
            
            if (isHRAdmin && results[2]) {
                setCompanyRequests(mapDocs(results[2]));
            }

        } catch (e) {
            console.error("Error fetching requests:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [appId, userProfile]);

    // 2. Handle Actions (Approve/Deny)
    const handleAction = async (req, status) => {
        if (!window.confirm(`Are you sure you want to ${status} this request?`)) return;
        
        try {
            await updateDoc(doc(db, `artifacts/${appId}/schedule_deviations`, req.id), {
                status: status,
                actionedBy: userProfile.id,
                actionedAt: serverTimestamp()
            });
            
            const updatedReq = { ...req, status, actionedAt: new Date(), actionedBy: userProfile.id };
            
            // Update all local states immediately
            const updateList = (list) => list.map(r => r.id === req.id ? updatedReq : r);
            
            setIncomingRequests(prev => updateList(prev));
            setMyRequests(prev => updateList(prev));
            if (isHRAdmin) setCompanyRequests(prev => updateList(prev));

            alert(`Request ${status}.`);
        } catch (e) {
            console.error("Error updating request:", e);
            alert("Action failed. Please try again.");
        }
    };

    // 3. Calculate Badge Count (Pending Only)
    // We filter the FULL incoming list to count only what needs action
    const pendingCount = useMemo(() => {
        return incomingRequests.filter(r => r.status === 'Pending').length;
    }, [incomingRequests]);

    // 4. Process Data (Filter, Sort)
    const processedData = useMemo(() => {
        let data = [];
        if (activeTab === 'incoming') data = incomingRequests;
        else if (activeTab === 'outgoing') data = myRequests;
        else if (activeTab === 'company') data = companyRequests;

        // Filter by Search
        if (searchTerm) {
            const lowerQ = searchTerm.toLowerCase();
            data = data.filter(r => 
                r.userName.toLowerCase().includes(lowerQ) || 
                (r.reason && r.reason.toLowerCase().includes(lowerQ))
            );
        }

        // Sort
        data.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (aVal && aVal.seconds) aVal = aVal.seconds;
            if (bVal && bVal.seconds) bVal = bVal.seconds;

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return data;
    }, [incomingRequests, myRequests, companyRequests, activeTab, searchTerm, sortConfig]);

    // 5. Pagination Logic
    const totalPages = Math.ceil(processedData.length / itemsPerPage);
    const paginatedData = processedData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            
            {/* Header & Tabs */}
            <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-4 border-b border-gray-200 pb-0">
                <div className="flex space-x-1 overflow-x-auto">
                    <button 
                        onClick={() => { setActiveTab('incoming'); setCurrentPage(1); }}
                        className={`px-4 py-3 text-sm font-bold border-b-2 transition whitespace-nowrap ${activeTab === 'incoming' ? 'text-[#213368] border-[#213368] bg-blue-50' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
                    >
                        {/* Display "To Approve (3)" but show full history in table */}
                        To Approve ({pendingCount})
                    </button>
                    <button 
                        onClick={() => { setActiveTab('outgoing'); setCurrentPage(1); }}
                        className={`px-4 py-3 text-sm font-bold border-b-2 transition whitespace-nowrap ${activeTab === 'outgoing' ? 'text-[#213368] border-[#213368] bg-blue-50' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
                    >
                        My Request History
                    </button>
                    {isHRAdmin && (
                        <button 
                            onClick={() => { setActiveTab('company'); setCurrentPage(1); }}
                            className={`px-4 py-3 text-sm font-bold border-b-2 transition whitespace-nowrap ${activeTab === 'company' ? 'text-[#213368] border-[#213368] bg-blue-50' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
                        >
                            Company Requests
                        </button>
                    )}
                </div>

                {/* Search */}
                <div className="relative w-full md:w-64 mb-2 md:mb-0">
                    <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input 
                        type="text" 
                        placeholder="Search requests..." 
                        className="w-full pl-9 pr-4 py-2 border rounded text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[400px]">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                        <tr>
                            <th onClick={() => requestSort('status')} className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100">Status</th>
                            <th onClick={() => requestSort('userName')} className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100">Employee</th>
                            <th onClick={() => requestSort('date')} className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100">Request Details</th>
                            <th className="px-6 py-3 text-left">Compensatory</th>
                            <th onClick={() => requestSort('createdAt')} className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100">Submitted On</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {paginatedData.length === 0 ? (
                            <tr><td colSpan="6" className="p-8 text-center text-gray-400 italic">No requests found.</td></tr>
                        ) : (
                            paginatedData.map((req) => (
                                <tr key={req.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wide
                                            ${req.status === 'Approved' ? 'bg-green-100 text-green-800' : 
                                              req.status === 'Denied' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}
                                        `}>
                                            {req.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-bold text-[#213368]">{req.userName}</div>
                                        {req.reason && (
                                            <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]" title={req.reason}>"{req.reason}"</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium text-gray-700">{formatDateLocal(new Date(req.date))}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded border 
                                                ${req.type === 'WFO' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-green-50 text-green-700 border-green-200'}
                                            `}>
                                                {req.type}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {req.compensatoryDate ? (
                                            <div className="flex items-center text-orange-600 bg-orange-50 px-2 py-1 rounded w-fit text-xs font-medium">
                                                <ClockIcon className="h-3 w-3 mr-1" />
                                                {formatDateLocal(new Date(req.compensatoryDate))}
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <div>{formatTimestamp(req.createdAt)}</div>
                                        {req.actionedAt && (
                                            <div className="text-[10px] text-gray-400 mt-1">
                                                {req.status} {formatTimestamp(req.actionedAt)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        {/* ACTION BUTTON LOGIC:
                                            1. Status MUST be Pending.
                                            2. Tab MUST be 'incoming' (Assigned to Me) OR 'company' (Admin Oversight).
                                            3. If 'company' tab, only Admins/HR can act (which is already protected by isHRAdmin check for tab visibility).
                                        */}
                                        {(activeTab === 'incoming' || activeTab === 'company') && req.status === 'Pending' ? (
                                            <div className="flex justify-end space-x-2">
                                                <button 
                                                    onClick={() => handleAction(req, 'Approved')} 
                                                    className="text-green-600 hover:text-green-800 hover:bg-green-50 p-1.5 rounded transition" 
                                                    title="Approve"
                                                >
                                                    <CheckCircleIcon className="h-6 w-6" />
                                                </button>
                                                <button 
                                                    onClick={() => handleAction(req, 'Denied')} 
                                                    className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition" 
                                                    title="Deny"
                                                >
                                                    <XCircleIcon className="h-6 w-6" />
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-gray-300 text-xs italic">
                                                {req.status === 'Pending' ? 'Waiting...' : 'Closed'}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <span className="text-xs text-gray-500">
                        Page {currentPage} of {totalPages}
                    </span>
                    <div className="flex space-x-2">
                        <button 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        >
                            <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <button 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        >
                            <ChevronRightIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}